// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-2 — asp-pot public/privacy-banner.js: the HIPAA + CCPA ribbon fixed to the top of every
// page (gradient 90deg #0B2A4A→#0078D4, white text, white "I understand" button) until the user
// acknowledges the current policy version. The ASP app kept the acknowledgement in localStorage
// keyed by version; OwlRx has a per-user record (POST/GET /policy-acceptances, versioned by
// LegalPages POLICY_VERSION), so "I understand" is recorded server-side with a localStorage cache
// so the ribbon does not flash on every page load. Storage failures are swallowed (private
// window): the ribbon simply shows again next visit.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import * as api from '../../../api/client'
import { useAuth } from '../../../auth/AuthContext'
import { POLICY_VERSION } from '../../legal/LegalPages'
import { readLocal, writeLocal } from '../potApi'

const STORAGE_KEY = 'potPrivacyAck'
const PAD_ATTR = 'data-pot-pb-padded'
interface AckRecord { version: string; ackedAt: string; account?: string }

export default function PrivacyBanner() {
  const { me } = useAuth()
  const account = me?.account_id ?? ''
  const [show, setShow] = useState(false)
  const [hiding, setHiding] = useState(false)

  useEffect(() => {
    if (!me?.authenticated) { setShow(false); return }
    const rec = readLocal<AckRecord>(STORAGE_KEY)
    if (rec && rec.version === POLICY_VERSION && (!rec.account || rec.account === account)) { setShow(false); return }
    let alive = true
    api.getPolicyAcceptances().then(r => {
      if (!alive) return
      const acked = r.acceptances.some(a => a.document === 'privacy' && a.version === POLICY_VERSION)
      if (acked) { const saved: AckRecord = { version: POLICY_VERSION, ackedAt: new Date().toISOString(), account }; writeLocal(STORAGE_KEY, saved) }
      setShow(!acked)
    }).catch(() => { if (alive) setShow(true) })
    return () => { alive = false }
  }, [account, me?.authenticated])

  // Push the page down so the ribbon does not cover the 48px POT header (privacy-banner.js did the same).
  useEffect(() => {
    if (!show) return
    const body = document.body
    if (!body.getAttribute(PAD_ATTR)) {
      const current = parseInt(getComputedStyle(body).paddingTop) || 0
      body.style.paddingTop = `${current + 64}px`
      body.setAttribute(PAD_ATTR, '1')
    }
    return () => {
      if (body.getAttribute(PAD_ATTR) === '1') { body.style.paddingTop = ''; body.removeAttribute(PAD_ATTR) }
    }
  }, [show])

  const ack = () => {
    const saved: AckRecord = { version: POLICY_VERSION, ackedAt: new Date().toISOString(), account }
    writeLocal(STORAGE_KEY, saved)
    api.recordPolicyAcceptance('privacy', POLICY_VERSION).catch(() => { /* recorded locally; the server copy lands next time */ })
    setHiding(true)
    setTimeout(() => { setShow(false); setHiding(false) }, 280)
  }

  if (!show) return null
  const link = { color: '#fff', textDecoration: 'underline', fontWeight: 600 } as const
  return (
    <div data-pot-privacy-banner role="region" aria-label="Privacy acknowledgement" className="pot-print-hide"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, background: 'linear-gradient(90deg,#0B2A4A 0%,#0078D4 100%)', color: '#fff', padding: '10px 16px', fontSize: 13, lineHeight: 1.4, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", boxShadow: '0 2px 6px rgba(0,0,0,.15)', display: 'flex', alignItems: 'center', gap: 14, justifyContent: 'center', flexWrap: 'wrap', transition: 'opacity .25s, transform .25s', opacity: hiding ? 0 : 1, transform: hiding ? 'translateY(-100%)' : 'none' }}>
      <div style={{ flex: 1, minWidth: 240, maxWidth: 1000 }}>
        <strong>🔒 HIPAA-Protected System.</strong> This platform contains Protected Health Information (PHI). All access is logged. By continuing, you acknowledge our{' '}
        <Link to="/legal/privacy" style={link}>Privacy Policy</Link>, <Link to="/legal/terms" style={link}>Terms of Service</Link>, and <Link to="/legal/data-rights" style={link}>Notice of Privacy Practices</Link>.
      </div>
      <button type="button" onClick={ack} style={{ background: '#fff', color: '#0B2A4A', border: 'none', padding: '7px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0, fontFamily: 'inherit' }}>I understand</button>
    </div>
  )
}
